/**
 * RESPONSIBILITY: the @StringList control — the vocabulary a @Choice({in}) points at.
 *
 * Plain strings, added and removed. Deliberately not the @List control: a row there is an
 * object with columns, and giving a bare string the same chrome makes eight event
 * categories look like eight records.
 *
 * Registers with a tester + rank. The highest-ranked renderer for a schema node wins, so
 * adding a control is a registration — there is no switch on field type.
 */
import { useState } from 'react';
import { rankWith, optionIs, type ControlProps } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';

function StringListControlBase({ data, handleChange, path, label, schema, errors }: ControlProps) {
  const [draft, setDraft] = useState('');
  const values: string[] = Array.isArray(data) ? (data as string[]) : [];
  const maxItems = (schema as { maxItems?: number }).maxItems;
  const full = maxItems !== undefined && values.length >= maxItems;

  // Duplicates are silently ignored rather than rejected: this is a set of allowed
  // values, and adding one twice is a slip, not something to interrupt an editor for.
  function add() {
    const v = draft.trim();
    if (!v || full || values.includes(v)) { setDraft(''); return; }
    handleChange(path, [...values, v]);
    setDraft('');
  }

  return (
    <div className="ctl">
      <label className="ctl-label">{label}</label>

      <div className="ctl-chips">
        {values.map((v) => (
          <span key={v} className="ctl-chip">
            {v}
            <button
              type="button" className="ctl-chip-x"
              onClick={() => handleChange(path, values.filter((x) => x !== v))}
              title={`Remove ${v}`} aria-label={`Remove ${v}`}
            >✕</button>
          </span>
        ))}
      </div>

      <div className="ctl-chip-add">
        <input
          type="text" className="ctl-input" value={draft} disabled={full}
          placeholder={full ? `Full (${maxItems})` : 'Add a value'}
          onChange={(e) => setDraft(e.target.value)}
          // Enter would submit the dialog's form and save the section instead.
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <button type="button" className="ctl-add" onClick={add} disabled={full}>+ Add</button>
      </div>

      {errors && <p className="ctl-error">{errors}</p>}
    </div>
  );
}

export const stringListControl = {
  tester: rankWith(10, optionIs('widget', 'stringlist')),
  renderer: withJsonFormsControlProps(StringListControlBase),
};
